import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import type { NodeRendererProps } from 'react-arborist';
import { ChevronDown, ChevronRight, Search, X, Bookmark, BookmarkPlus, Trash2 } from 'lucide-react';
import type { NodeTreeItem } from '@/stores/scope';

type SavedView = { id: string; name: string; storeIds: string[] };

type Props = {
  /** 選択候補のノードツリー (呼び出し元は通常 useScopeStore.roots、または NodeSwitcher の選択ノード
   *  配下に絞り込んだサブツリーを渡す)。'store' 以外のノード種別 (team 等) は内部で自動的に除外する
   *  (docs/design/v025-config-ui.md §2-5)。 */
  nodes: NodeTreeItem[];
  /** 選択中の店舗ノードID集合 (node_type_code='store' の node_id)。backfill後の invariant により
   *  store_id と同値 (apps/api/src/lib/scope.ts 参照) — 呼び出し元インターフェース (店舗ID配列) は
   *  tree 化前と互換のまま維持する。 */
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  colorOf: (storeId: string) => string;
  /** localStorage key の名前空間 (v1.0 マルチテナント化時に tenant/user プレフィックスを追加予定) */
  savedViewNamespace?: string;
};

const MAX_VIEW_NAME_LENGTH = 40;

const savedViewsKey = (ns: string) => `kpi-dashboard:saved-views:${ns}`;

const loadSavedViews = (ns: string): SavedView[] => {
  try {
    const raw = localStorage.getItem(savedViewsKey(ns));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v: unknown): v is SavedView => {
      if (!v || typeof v !== 'object') return false;
      const o = v as Record<string, unknown>;
      return typeof o.id === 'string' && typeof o.name === 'string'
        && Array.isArray(o.storeIds)
        && o.storeIds.every((id: unknown) => typeof id === 'string');
    });
  } catch {
    return [];
  }
};

const persistSavedViews = (ns: string, views: SavedView[]): boolean => {
  try {
    localStorage.setItem(savedViewsKey(ns), JSON.stringify(views));
    return true;
  } catch {
    return false; // Private Mode / QuotaExceeded
  }
};

const sanitizeViewName = (raw: string): string => {
  // 制御文字を除去して trim、最大長でカット
  return raw.replace(/[\x00-\x1F\x7F]/g, '').trim().slice(0, MAX_VIEW_NAME_LENGTH);
};

const normalize = (s: string) => s.toLowerCase().replace(/[\s　]+/g, '');

/** サブツリーに 'store' ノードを含むものだけを残す。team 等の非店舗ノードは KPI 比較対象外のため
 *  自身が store でなく、配下にも store が無ければツリーから除外する。store ノード自身の children
 *  (将来のチーム階層等) は比較選択の対象外として常に切り落とす。 */
export const pruneToStoreBearing = (nodes: NodeTreeItem[]): NodeTreeItem[] =>
  nodes.reduce<NodeTreeItem[]>((acc, n) => {
    if (n.node_type_code === 'store') {
      acc.push({ ...n, children: [] });
      return acc;
    }
    const children = pruneToStoreBearing(n.children);
    if (children.length > 0) acc.push({ ...n, children });
    return acc;
  }, []);

/** ノード配下 (自身含む) の店舗ノードID一覧を収集する */
export const collectStoreIds = (node: NodeTreeItem): string[] =>
  node.node_type_code === 'store' ? [node.node_id] : node.children.flatMap(collectStoreIds);

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

export const checkStateOf = (node: NodeTreeItem, selectedIds: Set<string>): CheckState => {
  const storeIds = collectStoreIds(node);
  if (storeIds.length === 0) return 'unchecked';
  const selectedCount = storeIds.filter((id) => selectedIds.has(id)).length;
  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === storeIds.length) return 'checked';
  return 'indeterminate';
};

const matchesQuery = (node: NodeTreeItem, query: string) =>
  normalize(node.name).includes(query) || normalize(node.node_code).includes(query);

type NodeCounts = { selectedCount: number; totalCount: number; checkState: CheckState };

/** checkStateOf/collectStoreIds と等価な判定結果を、ツリー全体を1回だけ辿るボトムアップ集計で作る。
 *  659店舗規模のテナントで、行ごとに checkStateOf(O(サブツリー)) を毎レンダー呼ぶと
 *  上位ノードの行だけで数百回の再帰が発生し描画のたびに固まっていたため導入 (2026-08-11)。 */
export const buildNodeCounts = (
  node: NodeTreeItem,
  selectedIds: Set<string>,
  map: Map<string, NodeCounts>,
): { selectedCount: number; totalCount: number } => {
  if (node.node_type_code === 'store') {
    const selectedCount = selectedIds.has(node.node_id) ? 1 : 0;
    map.set(node.node_id, { selectedCount, totalCount: 1, checkState: selectedCount ? 'checked' : 'unchecked' });
    return { selectedCount, totalCount: 1 };
  }
  let selectedCount = 0;
  let totalCount = 0;
  for (const child of node.children) {
    const c = buildNodeCounts(child, selectedIds, map);
    selectedCount += c.selectedCount;
    totalCount += c.totalCount;
  }
  const checkState: CheckState = totalCount === 0 || selectedCount === 0
    ? 'unchecked'
    : selectedCount === totalCount ? 'checked' : 'indeterminate';
  map.set(node.node_id, { selectedCount, totalCount, checkState });
  return { selectedCount, totalCount };
};

type StoreTreeRowProps = {
  node: NodeRendererProps<NodeTreeItem>['node'];
  style: NodeRendererProps<NodeTreeItem>['style'];
  checkState: CheckState;
  selectedCount: number;
  totalCount: number;
  color: string | null;
  onToggle: (node: NodeTreeItem) => void;
};

/** モジュールスコープで1度だけ定義し React.memo でラップする。StoreMultiSelect の内部関数として
 *  定義すると再レンダーのたびに別コンポーネント扱いになり、react-arborist が可視行を毎回
 *  アンマウント/リマウントしてしまう (props が同じでも React.memo の効果が出ない) ため (2026-08-11)。 */
const StoreTreeRow = memo(function StoreTreeRow({
  node, style, checkState, selectedCount, totalCount, color, onToggle,
}: StoreTreeRowProps) {
  return (
    <div
      style={style}
      className="flex items-center gap-1.5 px-1 rounded hover:bg-neutral-panel cursor-pointer"
      onClick={() => onToggle(node.data)}
    >
      {node.isInternal ? (
        <button
          type="button"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] text-neutral-sub shrink-0 sm:min-w-0 sm:min-h-0 sm:p-0.5"
          aria-label={node.isOpen ? '折り畳む' : '開く'}
        >
          {node.isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : (
        <span className="w-[44px] shrink-0 sm:w-[18px]" />
      )}
      <label
        className="flex items-center justify-center min-w-[44px] min-h-[44px] shrink-0 cursor-pointer sm:min-w-0 sm:min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          tabIndex={-1}
          ref={(el) => { if (el) el.indeterminate = checkState === 'indeterminate'; }}
          checked={checkState === 'checked'}
          onChange={() => onToggle(node.data)}
          className="w-4 h-4 accent-brand-red cursor-pointer"
          aria-label={`${node.data.name} を選択`}
        />
      </label>
      {color && (
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      )}
      <span className="truncate text-sm">{node.data.name}</span>
      {node.isInternal && (
        <span className="text-[11px] text-neutral-sub ml-auto shrink-0 pl-2 tabular-nums">
          {selectedCount} / {totalCount}
        </span>
      )}
    </div>
  );
});

/** 店舗多選択 UI (検索 + tree チェックボックス + 保存済みビュー)。
 *  v0.2.5 S-10 (T-1001) で react-arborist ベースの tree UI に置換 (docs/design/v025-config-ui.md §2-5)。
 *  Radix DropdownMenu (role="menu") と ARIA tree の role 混在を避けるため、旧実装の DropdownMenu では
 *  なく NodeSwitcher (S-9) と同じ「独自 state + backdrop クリック + Escape + フォーカストラップ」の
 *  軽量パネルパターンを踏襲する (新規に Popover 依存を追加しない、UI-C2 対応の踏襲)。 */
export function StoreMultiSelect({ nodes, selectedIds, onChange, colorOf, savedViewNamespace = 'store-comparison' }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews(savedViewNamespace));
  const [savingName, setSavingName] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // モバイル (sm 未満) では tree 行の tap ターゲットを 44×44 確保するため行高を切り替える
  // (docs/design/v025-config-ui.md §5)。react-arborist の rowHeight は数値 prop のため
  // Tailwind の sm: だけでは表現できず、matchMedia で明示的に判定する。
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const storeBearingNodes = useMemo(() => pruneToStoreBearing(nodes), [nodes]);
  const allStoreIds = useMemo(() => storeBearingNodes.flatMap(collectStoreIds), [storeBearingNodes]);
  // 検索1文字ごとにツリー全体の再走査(自前walk + react-arborist内部のsearchMatch)が二重に
  // 発火し入力がカクつくため、入力自体は即時反映しつつ重い絞り込み計算だけ低優先度に遅延させる
  // (React 18 useDeferredValue、2026-08-11)
  const deferredQuery = useDeferredValue(query);
  const matchedStoreIds = useMemo(() => {
    const q = normalize(deferredQuery);
    if (!q) return allStoreIds;
    const ids: string[] = [];
    const walk = (list: NodeTreeItem[]) => {
      for (const n of list) {
        if (n.node_type_code === 'store') {
          if (matchesQuery(n, q)) ids.push(n.node_id);
        } else {
          walk(n.children);
        }
      }
    };
    walk(storeBearingNodes);
    return ids;
  }, [storeBearingNodes, allStoreIds, deferredQuery]);

  // 各ノードの選択状態 (checked/indeterminate/unchecked) とチェック数を、ツリー全体を1回だけ辿って
  // 事前計算する (buildNodeCounts 参照)。可視行の描画では Map の O(1) 参照のみで済ませる。
  const nodeStatsByNode = useMemo(() => {
    const map = new Map<string, NodeCounts>();
    storeBearingNodes.forEach((n) => buildNodeCounts(n, selectedIds, map));
    return map;
  }, [storeBearingNodes, selectedIds]);

  // パネル開時に検索欄へフォーカス、閉時にクエリクリア
  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const handle = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(handle);
  }, [open]);

  // 外側クリック / Escape で閉じる + フォーカストラップ (NodeSwitcher と同一パターン)
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  const toggleNode = useCallback((node: NodeTreeItem) => {
    // クリックは行操作の都度(まれ)にしか発生しないため、ここでは従来通り collectStoreIds の
    // O(サブツリー)再帰で実IDを取得してよい (負荷が問題なのは「全可視行を毎レンダー計算」する側)
    const storeIds = collectStoreIds(node);
    const state = nodeStatsByNode.get(node.node_id)?.checkState ?? 'unchecked';
    const next = new Set(selectedIds);
    if (state === 'checked') {
      storeIds.forEach((id) => next.delete(id));
    } else {
      storeIds.forEach((id) => next.add(id));
    }
    onChange(next);
  }, [selectedIds, onChange, nodeStatsByNode]);
  const selectAllMatched = () => onChange(new Set([...selectedIds, ...matchedStoreIds]));
  const deselectAllMatched = () => {
    const next = new Set(selectedIds);
    matchedStoreIds.forEach((id) => next.delete(id));
    onChange(next);
  };
  const invertMatched = () => {
    const next = new Set(selectedIds);
    matchedStoreIds.forEach((id) => { if (next.has(id)) next.delete(id); else next.add(id); });
    onChange(next);
  };

  // 保存済みビューは店舗ID配列をそのまま保持するため、無効なIDの除去は呼び出し元
  // (StoreComparisonPage の allStores ベースの cleanup effect) に委ねる。ここでの絞り込みは行わない
  // — NodeSwitcher のスコープ絞り込みで一時的に候補外になった店舗を含むビューも復元できるようにするため。
  const applyView = (view: SavedView) => onChange(new Set(view.storeIds));
  const saveCurrentAsView = (rawName: string) => {
    const name = sanitizeViewName(rawName);
    if (!name) { setSaveError('ビュー名を入力してください'); return; }
    // 同名は上書き
    const existing = savedViews.findIndex((v) => v.name === name);
    const view: SavedView = { id: existing >= 0 ? savedViews[existing]!.id : `sv-${Date.now()}`, name, storeIds: Array.from(selectedIds) };
    const next = existing >= 0 ? savedViews.map((v, i) => (i === existing ? view : v)) : [...savedViews, view];
    setSavedViews(next);
    const ok = persistSavedViews(savedViewNamespace, next);
    if (!ok) { setSaveError('保存に失敗しました (ブラウザのストレージ制限)'); return; }
    setSavingName(null);
    setSaveError(null);
  };
  const deleteView = (viewId: string) => {
    const next = savedViews.filter((v) => v.id !== viewId);
    setSavedViews(next);
    persistSavedViews(savedViewNamespace, next);
  };

  // react-arborist の children には安定した参照を渡す必要がある (StoreTreeRow 自体は
  // モジュールスコープの memo 済みコンポーネント、ここでは行ごとの props 解決のみ行う)
  const renderRow = useCallback(
    ({ node, style }: NodeRendererProps<NodeTreeItem>) => {
      const stats = nodeStatsByNode.get(node.data.node_id);
      const color = node.data.node_type_code === 'store' ? colorOf(node.data.node_id) : null;
      return (
        <StoreTreeRow
          node={node}
          style={style}
          checkState={stats?.checkState ?? 'unchecked'}
          selectedCount={stats?.selectedCount ?? 0}
          totalCount={stats?.totalCount ?? 0}
          color={color}
          onToggle={toggleNode}
        />
      );
    },
    [nodeStatsByNode, colorOf, toggleNode],
  );

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`店舗フィルタ (${selectedIds.size}店舗選択中)`}
        className="inline-flex items-center gap-2 min-h-[44px] px-3 py-2 rounded border border-neutral-line bg-white text-sm hover:bg-neutral-panel focus-ring"
      >
        <span>
          店舗フィルタ <span className="tabular-nums text-brand-red font-semibold">({selectedIds.size}店舗選択中)</span>
        </span>
        <ChevronDown size={14} className="text-neutral-sub" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="店舗フィルタパネル"
          // モバイル (375px 想定) では画面下部からスライドインする bottom sheet として表示し、
          // sm 以上では従来通りトリガー直下の dropdown として表示する (docs/design/v025-config-ui.md §5)。
          // backdrop クリック / Escape / フォーカストラップは既存の useEffect をそのまま流用する。
          className="fixed inset-x-0 bottom-0 z-[55] max-h-[70vh] w-full overflow-y-auto rounded-t-lg border-t border-neutral-line bg-white shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:w-[380px] sm:max-w-[calc(100vw-2rem)] sm:max-h-none sm:overflow-hidden sm:rounded-lg sm:border"
        >
          {/* 保存済みビュー */}
          <div className="border-b border-neutral-line px-3 py-2 bg-neutral-panel">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1 text-xs text-neutral-sub">
                <Bookmark size={12} /> 保存済みビュー
              </div>
              {savingName === null ? (
                <button
                  type="button"
                  onClick={() => { setSavingName(''); setSaveError(null); }}
                  disabled={selectedIds.size === 0}
                  className="flex items-center gap-1 min-h-[28px] text-xs text-brand-red hover:underline disabled:text-neutral-line disabled:no-underline"
                >
                  <BookmarkPlus size={12} /> 現在の選択を保存
                </button>
              ) : (
                <div className="flex gap-1">
                  <input
                    autoFocus
                    value={savingName}
                    onChange={(e) => setSavingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveCurrentAsView(savingName); }
                      if (e.key === 'Escape') { e.preventDefault(); setSavingName(null); setSaveError(null); }
                    }}
                    maxLength={MAX_VIEW_NAME_LENGTH}
                    placeholder="ビュー名 (最大40文字)"
                    className="px-2 py-1 text-xs border border-neutral-line rounded focus-ring"
                  />
                  <button
                    type="button"
                    onClick={() => saveCurrentAsView(savingName)}
                    className="px-2 py-1 text-xs bg-brand-red text-white rounded hover:bg-brand-red-hover"
                  >
                    保存
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSavingName(null); setSaveError(null); }}
                    className="px-1 text-neutral-sub hover:text-brand-red"
                    aria-label="キャンセル"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            {saveError && <div className="text-xs text-warning-text mb-1">{saveError}</div>}
            {savedViews.length === 0 ? (
              <p className="text-xs text-neutral-sub">よく使う店舗セットを保存すると、ワンクリックで呼び出せます</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {savedViews.map((v) => (
                  <div key={v.id} className="inline-flex items-center gap-0.5 border border-neutral-line rounded bg-white text-xs">
                    <button
                      type="button"
                      onClick={() => applyView(v)}
                      className="min-h-[28px] px-2 py-1 hover:bg-neutral-panel rounded-l"
                      title={`${v.storeIds.length} 店舗`}
                    >
                      {v.name}
                      <span className="text-neutral-sub ml-1 tabular-nums">({v.storeIds.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`保存済みビュー「${v.name}」を削除しますか?`)) deleteView(v.id);
                      }}
                      className="min-h-[28px] px-1.5 py-1 text-neutral-sub hover:text-warning-text hover:bg-warning-bg rounded-r"
                      aria-label={`${v.name} を削除`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 検索 */}
          <div className="border-b border-neutral-line px-3 py-2 flex items-center gap-2">
            <Search size={14} className="text-neutral-sub flex-shrink-0" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }}
              placeholder="店舗名・ノード名で検索"
              className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-neutral-sub"
              aria-label="店舗検索"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-neutral-sub hover:text-brand-red"
                aria-label="検索クエリをクリア"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* tree 本体 (role="tree" 準拠、react-arborist 標準のキーボードナビ・仮想スクロール) */}
          <div className="px-1 py-1">
            {storeBearingNodes.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-neutral-sub">店舗がありません</div>
            ) : (
              <Tree<NodeTreeItem>
                data={storeBearingNodes}
                idAccessor="node_id"
                childrenAccessor="children"
                openByDefault={false}
                searchTerm={deferredQuery}
                searchMatch={(node, term) => matchesQuery(node.data, normalize(term))}
                width="100%"
                height={320}
                indent={16}
                rowHeight={isMobile ? 44 : 32}
                // 葉ノード (店舗) で Space/Enter を押すと react-arborist が select+activate を発火する
                // (キーボードのみでのチェック操作、WCAG 2.1.1 対応)。chevron button / checkbox は
                // tabIndex=-1 のため Tab では到達せず、行自体の roving tabindex 経由でのみ操作する。
                onActivate={(node) => toggleNode(node.data)}
              >
                {renderRow}
              </Tree>
            )}
          </div>

          {/* 一括アクション (表示中 = 検索クエリでの絞り込み結果が対象) */}
          <div className="border-t border-neutral-line px-3 py-2 flex items-center justify-between gap-2 text-xs bg-neutral-panel flex-wrap">
            <div className="text-neutral-sub tabular-nums">
              表示中 {matchedStoreIds.length} / 全 {allStoreIds.length}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={selectAllMatched} className="min-h-[28px] text-brand-red hover:underline">
                表示中を全選択
              </button>
              <button type="button" onClick={deselectAllMatched} className="min-h-[28px] text-neutral-sub hover:text-brand-red">
                表示中を全解除
              </button>
              <button type="button" onClick={invertMatched} className="min-h-[28px] text-neutral-sub hover:text-brand-red">
                表示中を反転
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
